'use client';

/**
 * Factor row used in the StockDetail pillar grid.
 * Renders weight badge, label, raw value, percentile bar, and percentile text.
 *
 * Percentile is post-inversion: p90 = green good contribution, p10 = red.
 */
export function FactorBar({ label, rawValue, zscore, percentile, invert, weight }: {
  label: string;
  rawValue: string;
  zscore: number | null;
  percentile: number | null;
  invert: boolean;
  weight?: number;
}) {
  const isNull = zscore === null;
  const p = percentile ?? 50;
  void invert; // logic lives upstream in getPercentile(key, invert)
  const aboveAvg = p >= 50;
  const barColor = isNull ? '#334155' : aboveAvg ? '#22c55e' : '#ef4444';

  const w = weight ?? 1;
  const weightColor = w >= 2 ? '#f59e0b' : '#475569';
  const weightBg = w >= 2 ? '#f59e0b20' : '#1e293b';

  return (
    <div className={`flex items-center gap-2 py-0.5 ${isNull ? 'opacity-40' : ''}`}>
      <span
        className="text-[8px] font-bold w-[14px] flex-shrink-0 text-center tabular-nums rounded-sm"
        style={{ color: weightColor, backgroundColor: weightBg, fontFamily: 'var(--font-mono)' }}
        title={`Weight ${w}× in pillar`}
      >
        {w}×
      </span>
      <span className="text-[10px] text-[#e2e8f0] w-[86px] flex-shrink-0 truncate">{label}</span>
      <span
        className="text-[9px] text-white w-[52px] flex-shrink-0 text-right tabular-nums"
        style={{ fontFamily: 'var(--font-mono)' }}
      >
        {rawValue}
      </span>
      <div className="flex-1 h-2 bg-[#1e293b] rounded-full relative overflow-hidden min-w-[80px]">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[5px] h-[5px] rotate-45 bg-[#64748b] z-10" />
        {!isNull && (
          <div
            className="absolute top-0 bottom-0 rounded-sm transition-all duration-300"
            style={{
              left: aboveAvg ? '50%' : `${p}%`,
              width: `${Math.abs(p - 50)}%`,
              backgroundColor: barColor,
              opacity: 0.8,
            }}
          />
        )}
      </div>
      <span
        className="text-[8px] text-white w-[28px] flex-shrink-0 text-right tabular-nums"
        style={{ fontFamily: 'var(--font-mono)' }}
      >
        {isNull ? 'N/A' : percentile !== null ? `p${percentile}` : '--'}
      </span>
    </div>
  );
}
